import React, { useEffect, useRef, useState } from "react";
import { FaCamera, FaMapMarkerAlt } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const MarkPopup = ({ onClose, staffId, type, userMobile, config }) => {
    const [location, setLocation] = useState(null);
    const [locationError, setLocationError] = useState(null);
    const [loading, setLoading] = useState(false);
    const videoRef = useRef(null);
    const streamRef = useRef(null);

    // Start webcam
    useEffect(() => {
        async function startCamera() {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: true,
                });
                streamRef.current = stream;
                if (videoRef.current) {
                    videoRef.current.srcObject = stream;
                    videoRef.current.play();
                }
            } catch (err) {
                setLocationError("Camera access error");
                toast.error(
                    "Camera access denied. Please allow camera permissions."
                );
            }
        }
        startCamera();
        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach((track) => track.stop());
            }
        };
    }, []);

    // Get precise browser location once (for UI feedback)
    useEffect(() => {
        const geoOptions = {
            enableHighAccuracy: true, // Request precise location
            timeout: 100000, // 10 seconds timeout
            maximumAge: 0, // Don't use cached position
        };

        navigator.geolocation.getCurrentPosition(
            (pos) => {
                setLocation({
                    latitude: pos.coords.latitude,
                    longitude: pos.coords.longitude,
                    accuracy: pos.coords.accuracy, // Show accuracy in meters
                });
            },
            (error) => {
                let errorMessage = "Unable to fetch location";
                switch (error.code) {
                    case error.PERMISSION_DENIED:
                        errorMessage = "Location access denied by user";
                        break;
                    case error.POSITION_UNAVAILABLE:
                        errorMessage = "Location information unavailable";
                        break;
                    case error.TIMEOUT:
                        errorMessage = "Location request timed out";
                        break;
                    default:
                        errorMessage = "An unknown error occurred";
                        break;
                }
                setLocationError(errorMessage);
                toast.warn(`Location Error: ${errorMessage}`);
            },
            geoOptions
        );
    }, []);

    const handleConfirm = async () => {
        setLoading(true);

        const token = localStorage.getItem("token");
        const bid = localStorage.getItem("branch_id");
        const selectedDate = new Date().toISOString().split("T")[0];
        const selectedTime = new Date().toLocaleTimeString([], {
            hour12: false,
        });

        // Capture precise location before submit
        let currentLocation;
        try {
            const geoOptions = {
                enableHighAccuracy: true, // Request precise location for submission
                timeout: 15000, // 15 seconds timeout for submission
                maximumAge: 0, // Fresh location required
            };

            currentLocation = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    (pos) =>
                        resolve({
                            latitude: pos.coords.latitude,
                            longitude: pos.coords.longitude,
                            accuracy: pos.coords.accuracy,
                        }),
                    (err) => reject(err),
                    geoOptions
                );
            });

            // Check if location accuracy is acceptable (optional)
            if (currentLocation.accuracy > 100) {
                // 100 meters threshold
                toast.warn(
                    `Location accuracy is low (${currentLocation.accuracy.toFixed(
                        0
                    )}m). Please move to a better location.`
                );
            }

            setLocation(currentLocation);
            setLocationError(null);
        } catch (err) {
            let errorMessage = "Location access denied or unavailable.";
            switch (err.code) {
                case err.PERMISSION_DENIED:
                    errorMessage =
                        "Location access denied. Please enable location services.";
                    break;
                case err.POSITION_UNAVAILABLE:
                    errorMessage =
                        "Location information unavailable. Check your GPS signal.";
                    break;
                case err.TIMEOUT:
                    errorMessage =
                        "Location request timed out. Please try again.";
                    break;
            }
            setLocationError(errorMessage);
            toast.error(errorMessage);
            setLoading(false);
            return;
        }

        // Take selfie snapshot
        let photoBlob = null;
        if (videoRef.current) {
            const canvas = document.createElement("canvas");
            const video = videoRef.current;
            canvas.width = video.videoWidth || 640;
            canvas.height = video.videoHeight || 480;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
            photoBlob = await new Promise((resolve) =>
                canvas.toBlob(resolve, "image/jpeg", 0.95)
            );
        } else {
            toast.error("No camera available. Cannot capture selfie.");
            setLoading(false);
            return;
        }

        // Prepare FormData
        const formData = new FormData();

        if (type === "markin") {
            formData.append("lat", currentLocation.latitude);
            formData.append("long", currentLocation.longitude);
            formData.append("photo", photoBlob, "selfie.jpg");
            formData.append(
                "json_data",
                JSON.stringify([
                    {
                        of_month: new Date().getMonth() + 1,
                        year: new Date().getFullYear(),
                        attend: true,
                        leave: false,
                        date: selectedDate,
                        in_time: selectedTime,
                    },
                ])
            );
        } else {
            formData.append("out_lat", currentLocation.latitude);
            formData.append("out_long", currentLocation.longitude);
            formData.append("photo", photoBlob, "selfie.jpg");
            formData.append("date", selectedDate);
            formData.append("out_time", selectedTime);
        }

        try {
            let url, method;
            if (type === "markin") {
                url = `${config.apiUrl}/api/swalook/staff/attendance/?branch_name=${bid}&staff_id=${userMobile}`;
                method = "POST";
            } else {
                url = `${config.apiUrl}/api/swalook/staff_attendance_mobile/punch-out/?branch_name=${bid}&staff_id=${userMobile}`;
                method = "POST";
            }

            const response = await fetch(url, {
                method: method,
                headers: {
                    Authorization: `Token ${token}`,
                },
                body: formData,
            });

            if (response.ok) {
                const result = await response.json();

                if (result.message === "staff attendance already exists") {
                    toast.info("Attendance already marked for today.");
                } else {
                    toast.success(
                        `${
                            type === "markin" ? "In-time" : "Out-time"
                        } marked successfully!`
                    );
                    onClose();
                    setTimeout(() => {
                        window.location.reload();
                    }, 1500);
                }
            } else {
                const errorJSON = await response.json();
                const errorMessage =
                    errorJSON?.errors?.date ||
                    errorJSON?.message ||
                    "Failed to mark attendance";
                toast.error(errorMessage);
            }
        } catch (error) {
            console.error("Error marking attendance:", error);
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    const reset = () => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach((t) => t.stop());
        }
        onClose();
    };

    return (
        <>
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <div
                className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-end z-50"
                style={{ zoom: 1.33 }}
            >
                <div className="bg-white w-full rounded-t-2xl p-6">
                    <h3 className="font-semibold text-lg mb-4">
                        {type === "markin" ? "Marking In" : "Marking Out"}
                    </h3>

                    <div className="flex justify-around mb-6">
                        <div className="text-center">
                            <FaMapMarkerAlt className="text-red-500 text-3xl mx-auto mb-2" />
                            <p className="text-sm font-semibold">Location</p>
                            {locationError ? (
                                <p className="text-xs text-red-500">
                                    {locationError}
                                </p>
                            ) : (
                                <div className="text-xs text-gray-500">
                                    {location ? (
                                        <>
                                            <p>
                                                {location.latitude.toFixed(6)},{" "}
                                                {location.longitude.toFixed(6)}
                                            </p>
                                            <p className="text-green-600 mt-1">
                                                Accuracy:{" "}
                                                {location.accuracy.toFixed(0)}m
                                            </p>
                                        </>
                                    ) : (
                                        "Fetching precise location..."
                                    )}
                                </div>
                            )}
                        </div>
                        <div className="text-center">
                            <FaCamera className="text-orange-500 text-3xl mx-auto mb-2" />
                            <p className="text-sm font-semibold">Camera</p>
                            <p className="text-xs text-gray-400 mt-1">
                                Selfie will be captured on Confirm
                            </p>
                        </div>
                    </div>

                    <div className="flex justify-center mb-4">
                        <video
                            ref={videoRef}
                            width={200}
                            height={150}
                            className="rounded shadow border border-gray-200"
                            autoPlay
                            playsInline
                            muted
                        />
                    </div>

                    <p className="text-xs text-center text-gray-600 mb-4">
                        By continuing, you allow Swalook to capture your precise
                        location and selfie for attendance marking.
                    </p>

                    <button
                        disabled={loading}
                        onClick={handleConfirm}
                        className={`w-full py-2 text-white rounded-md text-sm font-medium ${
                            loading
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-blue-600 hover:bg-blue-700"
                        }`}
                    >
                        {loading
                            ? "Processing..."
                            : type === "markin"
                            ? "Confirm Mark In"
                            : "Confirm Mark Out"}
                    </button>

                    <button
                        onClick={reset}
                        className="w-full mt-3 py-2 text-sm text-gray-600 underline"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </>
    );
};

export default MarkPopup;
